// Libs
import type { SortDirection, TableCellProps } from "@mui/material";
import type { ClassValue } from "clsx";
import type React from "react";

// Utils
import type { FormatterOptions } from "src/utils/formatters";
import type { TableContextType } from "./TableContext";

export type { TableContextType } from "./TableContext";

export interface LocalTableDisplayConfiguration {
    activeDisplay: TableConfigurationDisplayEnum;
    customDisplay: Record<string, TableColumnDisplayConfiguration>;
}

export enum TableConfigurationDisplayEnum {
    Basic = "basic",
    Intermediary = "intermediary",
    Advanced = "advanced",
    Custom = "custom",
}

export interface TableColumnDisplayConfiguration {
    mainColumns: string[];
    extendedColumns?: string[];
    notVisible?: string[];
}

export interface TableGroupConfiguration {
    key: string;
    labelKey: string;
    bgColor: string;
    isHiddenInFooter?: boolean;
    displays: {
        [K in TableConfigurationDisplayEnum]?: TableColumnDisplayConfiguration;
    };
    adminColumns?: string[];
    displayOrder?: string[];
    hideColumnsLabel?: boolean;
    border?: string;
}

type RenderComponent<T> = ({ value, datum }: { value: any; datum: T }) => React.ReactNode;

export interface TableFormatterOptions extends FormatterOptions {
    mode: "body" | "footer" | "header";
}

export type TableComputedColumnConfiguration<T> =
    | T
    | ((formatOptions?: TableFormatterOptions, context?: Partial<TableContextType>) => T);

export interface TableColumnConfiguration<T = Record<string, unknown>, S = T> {
    key: string;
    align?: TableCellProps["align"];
    classes?: TableComputedColumnConfiguration<ClassValue>;
    DisplayComponent?: RenderComponent<T>;
    excelFormat?: string;
    FooterComponent?: RenderComponent<S>;
    formatter?: (value: any, datum?: T, formatOptions?: TableFormatterOptions) => string;
    formatterExcel?: (
        value: any,
        datum?: T,
        formatOptions?: TableFormatterOptions
    ) => string | number;
    footerValueKey?: string;
    helperTextKey?: TableComputedColumnConfiguration<string>;
    isSortable: boolean;
    labelKey: TableComputedColumnConfiguration<string>;
    excelLabelKey?: TableComputedColumnConfiguration<string>;
    width?: string | number;
}

export interface TableConfiguration<T = Record<string, unknown>, S = T> {
    groups: TableGroupConfiguration[];
    columns: TableColumnConfiguration<T, S>[];
    actions?: (datum: T) => React.ReactNode;
}

export type WithPaginationProps<P> = P & {
    count: number;
    page: number;
    limit: number;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
};

export type WithSortingProps<P> = P & {
    sorts: { key: string; direction: SortDirection }[];
    lastSortKey: string;
    onColumnSortToggle: (columnKey: string) => void;
};

export type WithActiveGroup<P> = P & {
    activeGroups: string[];
    onGroupToggle: (groupKey: string) => void;
};

export interface TableActionComponentProps<T = Record<string, unknown>> {
    datum: T;
}
